import React, { useEffect, useState } from 'react';
import { SimulatorModel } from '../../models/SimulatorModel';
import {
    SimulatorSettingsModel as SimulatorSettingsDto,
    SimulatorDetailsModel,
} from '../../models/superbit-api-models';
import { SETTINGS_TABS, SettingsTabKey } from './simulatorSettings.constants';
import GeneralSettings from './tabs/GeneralSettings';
import CompaniesSettings from './tabs/CompaniesSettings';
import DiscountsSettings from './tabs/DiscountsSettings';
import './SimulatorSettings.css';

/**
 * Simulator settings — React port of the old _SimulatorSettings.cshtml modal.
 * Slice 1: tab shell + the "כללי" (general/public) tab, wired end-to-end.
 * The remaining tabs are placeholders to be implemented in follow-up slices.
 */
const SimulatorSettings: React.FC = () => {
    const [api] = useState(() => new SimulatorModel());
    const [activeTab, setActiveTab] = useState<SettingsTabKey>('general');
    const [settings, setSettings] = useState<SimulatorSettingsDto | undefined>(undefined);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            setError(null);
            try {
                const data = await api.simulatorSettings();
                if (!cancelled) setSettings(data);
            } catch (e: any) {
                if (!cancelled)
                    setError(
                        `טעינת ההגדרות נכשלה${e?.status ? ` (קוד ${e.status})` : ''}`
                    );
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [api]);

    const handleSaveGeneral = async (details: SimulatorDetailsModel) => {
        await api.simulatorUpdateSimulatorDetails(details);
        setSettings((prev) => {
            if (!prev) return prev;
            const next = SimulatorSettingsDto.fromJS(prev.toJSON());
            next.simulatorDetailsModel = details;
            return next;
        });
    };

    const renderPanel = () => {
        if (loading) {
            return <div className="sbsim__state">טוען הגדרות...</div>;
        }
        if (error) {
            return <div className="sbsim__state sbsim__state--error">{error}</div>;
        }

        switch (activeTab) {
            case 'general':
                return (
                    <div className="sbsim__panel">
                        <GeneralSettings
                            details={settings?.simulatorDetailsModel}
                            onSave={handleSaveGeneral}
                        />
                    </div>
                );
            case 'companies':
                return settings ? (
                    <div className="sbsim__panel">
                        <CompaniesSettings api={api} settings={settings} />
                    </div>
                ) : null;
            case 'discounts':
                return settings ? (
                    <div className="sbsim__panel">
                        <DiscountsSettings api={api} settings={settings} />
                    </div>
                ) : null;
            default: {
                const label = SETTINGS_TABS.find((t) => t.key === activeTab)?.label ?? '';
                return (
                    <div className="sbsim__placeholder">
                        הלשונית "{label}" תיבנה בשלב הבא.
                    </div>
                );
            }
        }
    };

    return (
        <div className="sbsim">
            <h3 className="sbsim__title">הגדרות סימולטור</h3>

            <nav className="sbsim__tabs">
                {SETTINGS_TABS.map((tab) => {
                    const isActive = tab.key === activeTab;
                    const classes = [
                        'sbsim__tab',
                        isActive ? 'sbsim__tab--active' : '',
                        tab.ready ? '' : 'sbsim__tab--disabled',
                    ]
                        .filter(Boolean)
                        .join(' ');
                    return (
                        <button
                            key={tab.key}
                            type="button"
                            className={classes}
                            onClick={() => tab.ready && setActiveTab(tab.key)}
                            title={tab.ready ? undefined : 'בקרוב'}
                        >
                            {tab.label}
                        </button>
                    );
                })}
            </nav>

            {renderPanel()}
        </div>
    );
};

export default SimulatorSettings;
